import { useUserHasPermission } from '@/hooks/usePermissions';
import EmailPreview from '@/ui/components/previewAndSendSendableDocument/EmailPreview';
import {
  Alert,
  CheckboxGroupField,
  Field,
  Modal,
  ModalProps,
  Button as PLButton,
} from '@payaca/ambrosia';

import { buildEmailSubjectPrefixForProject } from '@payaca/helpers/dealHelper';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { singularPlural } from '@payaca/utilities/stringUtilities';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import useShareFolderEntries from '../../../../../../api/mutations/project/useShareFolderEntries';
import projectKeys from '../../../../../../api/queries/project/keyFactory';
import useGetProjectByIdOverview from '../../../../../../api/queries/project/useGetProjectByIdOverview';
import { TFolderEntry } from '../../../../../../api/queries/project/useGetProjectFolderEntries';
import { ProjectByIdQuery } from '../../../../../../gql/graphql';
import { useCompanyName } from '../../../../../../hooks/useCompanyName';
import { useProjectPrimaryContact } from '../../../../../../hooks/useProjectPrimaryContact';

type EmailBody = {
  intro: string;
  outro: string;
};

const buildEmailSubjectPrefixForProjectFromProject = (
  project: ProjectByIdQuery['projectById']
) => {
  return buildEmailSubjectPrefixForProject({
    reference: project.reference,
    siteAddress: {
      line1: project.addresses.site?.[0]?.line1 || undefined,
      line2: project.addresses.site?.[0]?.line2 || undefined,
    },
  });
};

type Props = {
  projectId: string;
  folderEntries: TFolderEntry[];
} & Pick<ModalProps, 'onClose'>;

const ShareFilesModalContent: FC<Props> = (props) => {
  const { folderEntries, projectId, onClose } = props;

  const [emailBody, setEmailBody] = useState<EmailBody>();
  const [showSelectCustomerContactAlert, setShowSelectCustomerContactAlert] =
    useState(false);

  const { project } = useGetProjectByIdOverview(projectId);
  const primaryContact = useProjectPrimaryContact(projectId);

  const [selectedCustomerContactIds, setSelectedCustomerContactIds] = useState<
    string[]
  >(primaryContact?.publicId ? [primaryContact.publicId] : []);

  const companyName = useCompanyName();
  const queryClient = useQueryClient();

  const { mutateAsync: shareFolderEntries, isPending: isLoading } =
    useShareFolderEntries({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.folderEntries(projectId),
        });
        void onClose?.();
      },
    });

  const canUnshareDealFiles = useUserHasPermission({
    permissions: [DealsPermissions.UNSHARE_DEAL_FILES],
  });

  useEffect(() => {
    if (!companyName || folderEntries.length === 0) return;

    setEmailBody(
      !canUnshareDealFiles ||
        project?.customer?.fileSharingSettings?.attachToEmails
        ? {
            intro: `${companyName} has shared the following files with you:`,
            outro: `Thanks,\n\n${companyName}`,
          }
        : {
            intro: `${companyName} has shared files with you via your client portal.\n\nPlease click the button below to access your client portal and view the files.`,
            outro: `Thanks,\n\n${companyName}`,
          }
    );
  }, [companyName, folderEntries, canUnshareDealFiles, project]);

  /**
   * Handles sharing files for deal
   */
  const handleShareFiles = async () => {
    if (folderEntries.length === 0) return;
    if (!selectedCustomerContactIds.length) {
      setShowSelectCustomerContactAlert(true);
      return;
    }
    await shareFolderEntries({
      email: { emailBody, toCustomerContactIds: selectedCustomerContactIds },
      folderEntryIds: folderEntries.map((entry) => entry.id),
    });
  };

  const handleTextChange = (key: keyof EmailBody) => (value: string) => {
    setEmailBody((s = { intro: '', outro: '' }) => ({
      ...s,
      [key]: value,
    }));
  };

  return (
    <>
      <Modal.Body>
        <div className="mb-4 space-y-3">
          <p className="text-base">
            <span className="font-medium">To:</span>{' '}
            {project?.customer?.contacts
              .filter((c) => selectedCustomerContactIds.includes(c.publicId))
              .map((c) => c.email)
              .join(', ')}
          </p>

          <Field.Legacy name="customerContacts">
            <CheckboxGroupField
              value={selectedCustomerContactIds}
              options={
                project?.customer?.contacts.map((c) => {
                  const isMissingEmail = !c.email;
                  return {
                    label: isMissingEmail ? (
                      <span className="text-base">
                        {c.name} (Missing email address)
                      </span>
                    ) : (
                      c.name
                    ),
                    value: c.publicId,
                    disabled: !c.email,
                    className: isMissingEmail ? 'cursor-not-allowed' : '',
                  };
                }) || []
              }
              onChange={(customerContacts) => {
                if (showSelectCustomerContactAlert) {
                  setShowSelectCustomerContactAlert(false);
                }
                setSelectedCustomerContactIds(customerContacts);
              }}
              emphasised
            />
          </Field.Legacy>

          <p className="text-base">
            <span className="font-medium">Subject:</span>{' '}
            {project && buildEmailSubjectPrefixForProjectFromProject(project)}
            {companyName} has shared{' '}
            {singularPlural(folderEntries.length, 'file', 'files', {
              singularlengthAlternative: 'a',
            })}{' '}
            with you
          </p>

          <EmailPreview
            emailCopy={{
              preButton: emailBody?.intro || '',
              postButton: emailBody?.outro || '',
            }}
            onChange={({ preButton, postButton }) => {
              if (preButton) {
                handleTextChange('intro')(preButton);
              }
              if (postButton) {
                handleTextChange('outro')(postButton);
              }
            }}
            buttonText="View files"
            hasButton={canUnshareDealFiles}
          >
            {(!canUnshareDealFiles ||
              project?.customer?.fileSharingSettings?.attachToEmails) && (
              <ul className="text-left text-base">
                {folderEntries.map((file) => (
                  <li key={file.id}>{getLiContent(file)}</li>
                ))}
              </ul>
            )}
          </EmailPreview>
        </div>

        <p className="mb-2 text-base">
          You are about to share {folderEntries.length}{' '}
          {folderEntries.length === 1 ? 'file' : 'files'} with the customer of
          this project.
        </p>

        <p className="mb-2 text-base">
          This will allow the customer to view and download the{' '}
          {folderEntries.length === 1 ? 'file' : 'files'} via{' '}
          {canUnshareDealFiles ? 'the client portal' : 'email'}.
        </p>

        {!canUnshareDealFiles && (
          <p className="mb-2 text-base">This action cannot be undone.</p>
        )}

        {showSelectCustomerContactAlert && (
          <Alert colour="red" className="mt-2">
            Please select at least one Contact to share the{' '}
            {singularPlural(folderEntries.length, 'File')} with.
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Modal.Footer.Actions>
          <PLButton
            className="mr-2"
            variant={'outline'}
            disabled={isLoading}
            onClick={onClose}
          >
            Cancel
          </PLButton>
          <PLButton
            onClick={handleShareFiles}
            disabled={isLoading}
            isProcessing={isLoading}
          >
            Share
          </PLButton>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </>
  );
};

const ShareFilesModal: FC<Props & Pick<ModalProps, 'isOpen'>> = (props) => {
  const { folderEntries, isOpen, onClose, projectId } = props;

  return (
    <Modal
      title="Are you sure you want to share?"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ShareFilesModalContent
        projectId={projectId}
        folderEntries={folderEntries}
        onClose={onClose}
      />
    </Modal>
  );
};

const getLiContent = (file: TFolderEntry) => {
  switch (file.__typename) {
    case 'DocumentFolderEntry':
      return <>{file.name} (attached to this email)</>;
    case 'UrlFolderEntry':
      return (
        <a href={file.url.url} target="_blank" rel="noreferrer">
          {file.name}
        </a>
      );
    case 'UploadFolderEntry':
      return (
        <a href={file.upload.url} target="_blank" rel="noreferrer">
          {file.name}
        </a>
      );
    case 'DocumentInstanceFolderEntry':
      return <>{file.name}</>;
    default:
      return <>Unsupported</>;
  }
};

export default ShareFilesModal;
